/**
 * Advanced Product Variant Manager
 * Handles complex variant logic, pricing, and inventory
 * Place in: assets/product-variant-manager.js
 */

class ProductVariantManager {
  constructor(productData) {
    this.product = productData;
    this.currentVariant = null;
    this.selections = {};
    this.priceModifiers = {};
    this.basePrice = 0;
    this.listeners = [];
    
    this.init();
  }
  
  init() {
    // Set base price from first variant
    if (this.product.variants && this.product.variants.length > 0) {
      this.basePrice = this.product.variants[0].price / 100;
      this.currentVariant = this.product.variants[0];
    }
    
    // Initialize selections with first option values
    this.product.options.forEach((option, index) => {
      if (option.values && option.values.length > 0) {
        this.selections[option.name.toLowerCase()] = option.values[0];
      }
    });
    
    this.updateVariant();
  }
  
  /**
   * Update selection for an option
   */
  updateSelection(optionName, value, priceModifier = 0) {
    this.selections[optionName.toLowerCase()] = value;
    this.priceModifiers[optionName.toLowerCase()] = priceModifier;
    
    this.updateVariant();
    this.updatePrice();
    this.notifyListeners();
    
    return this.currentVariant;
  }
  
  /**
   * Find matching variant based on current selections
   */
  updateVariant() {
    if (!this.product.variants) return;
    
    // Find variant that matches all selections
    const matchingVariant = this.product.variants.find(variant => {
      return this.product.options.every((option, index) => {
        const selectedValue = this.selections[option.name.toLowerCase()];
        const variantValue = variant[`option${index + 1}`];
        return selectedValue === variantValue;
      });
    });
    
    if (matchingVariant) {
      this.currentVariant = matchingVariant;
      return matchingVariant;
    }
    
    return null;
  }
  
  /**
   * Calculate total price with modifiers
   */
  calculatePrice() {
    const modifierTotal = Object.values(this.priceModifiers).reduce((sum, val) => sum + val, 0);
    return this.basePrice + modifierTotal;
  }
  
  /**
   * Update price display
   */
  updatePrice() {
    const totalPrice = this.calculatePrice();
    const comparePrice = this.product.compare_at_price ? this.product.compare_at_price / 100 : totalPrice * 1.25;
    const savings = comparePrice - totalPrice;
    
    // Update DOM elements
    this.updateElement('currentPrice', `£${totalPrice.toFixed(2)}`);
    this.updateElement('comparePrice', `£${comparePrice.toFixed(2)}`);
    this.updateElement('saveBadge', `SAVE £${savings.toFixed(2)}`);
    
    return {
      current: totalPrice,
      compare: comparePrice,
      savings: savings
    };
  }
  
  /**
   * Check if current variant is available
   */
  isAvailable() {
    return this.currentVariant && this.currentVariant.available;
  }
  
  /**
   * Get current variant ID
   */
  getVariantId() {
    return this.currentVariant ? this.currentVariant.id : null;
  }
  
  /**
   * Add to cart
   */
  async addToCart(quantity = 1, additionalProperties = {}) {
    if (!this.isAvailable()) {
      throw new Error('Selected variant is not available');
    }
    
    const formData = {
      id: this.getVariantId(),
      quantity: quantity,
      properties: {
        ...this.selections,
        ...additionalProperties
      }
    };
    
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }
      
      const data = await response.json();
      
      // Update cart count if element exists
      this.updateCartCount();
      
      return data;
    } catch (error) {
      console.error('Add to cart error:', error);
      throw error;
    }
  }
  
  /**
   * Update cart count in header
   */
  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      const cartCountElements = document.querySelectorAll('.cart-count');
      cartCountElements.forEach(el => {
        el.textContent = cart.item_count;
      });
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }
  
  /**
   * Helper to update DOM element
   */
  updateElement(id, content) {
    const element = document.getElementById(id);
    if (element) {
      element.textContent = content;
    }
  }
  
  /**
   * Add listener for variant changes
   */
  onChange(callback) {
    this.listeners.push(callback);
  }
  
  /**
   * Notify all listeners
   */
  notifyListeners() {
    this.listeners.forEach(callback => {
      callback({
        variant: this.currentVariant,
        selections: this.selections,
        price: this.calculatePrice()
      });
    });
  }
  
  /**
   * Get product images for current variant
   */
  getVariantImages() {
    if (!this.currentVariant || !this.currentVariant.featured_image) {
      return this.product.images || [];
    }
    
    // Return images starting with variant's featured image
    const variantImage = this.currentVariant.featured_image.src;
    const otherImages = this.product.images.filter(img => img.src !== variantImage);
    
    return [{ src: variantImage }, ...otherImages];
  }
  
  /**
   * Update main product image
   */
  updateMainImage() {
    const images = this.getVariantImages();
    if (images.length > 0) {
      const mainImage = document.getElementById('mainProductImage');
      if (mainImage) {
        mainImage.src = images[0].src;
      }
    }
  }
}

/**
 * Visual Option Selector Component
 */
class VisualOptionSelector {
  constructor(containerSelector, variantManager, optionName) {
    this.container = document.querySelector(containerSelector);
    this.variantManager = variantManager;
    this.optionName = optionName;
    this.selectedValue = null;
    
    this.init();
  }
  
  init() {
    if (!this.container) return;
    
    // Add click handlers to option cards
    const optionCards = this.container.querySelectorAll('.option-card');
    optionCards.forEach(card => {
      card.addEventListener('click', () => this.selectOption(card));
    });
  }
  
  selectOption(card) {
    const value = card.getAttribute('data-value');
    const priceModifier = parseFloat(card.getAttribute('data-price-modifier') || 0);
    
    // Remove selected state from all cards
    this.container.querySelectorAll('.option-card').forEach(c => {
      c.classList.remove('selected');
      const checkmark = c.querySelector('.checkmark');
      if (checkmark) checkmark.remove();
    });
    
    // Add selected state to clicked card
    card.classList.add('selected');
    
    // Add checkmark
    const checkmark = document.createElement('span');
    checkmark.className = 'checkmark';
    checkmark.textContent = '✓';
    card.appendChild(checkmark);
    
    // Update selected value display
    this.selectedValue = value;
    this.updateSelectedDisplay(value);
    
    // Update variant manager
    this.variantManager.updateSelection(this.optionName, value, priceModifier);
  }
  
  updateSelectedDisplay(value) {
    const displayId = `selected${this.optionName.charAt(0).toUpperCase() + this.optionName.slice(1)}`;
    const display = document.getElementById(displayId);
    if (display) {
      display.textContent = value;
    }
  }
}

/**
 * Initialize product configurator
 */
function initProductConfigurator(productData) {
  // Create variant manager
  const variantManager = new ProductVariantManager(productData);
  
  // Initialize option selectors
  const optionTypes = ['color', 'size', 'storage', 'headboard', 'base'];
  optionTypes.forEach(optionType => {
    new VisualOptionSelector(
      `.${optionType}-options`,
      variantManager,
      optionType
    );
  });
  
  // Handle form submission
  const form = document.getElementById('product-form');
  if (form) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      
      const button = form.querySelector('.add-to-cart-btn');
      const originalText = button.textContent;
      
      try {
        button.textContent = 'Adding...';
        button.disabled = true;
        
        await variantManager.addToCart(1);
        
        button.textContent = '✓ Added to Cart';
        
        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 2000);
        
        // Optional: Show cart drawer or redirect
        // window.location.href = '/cart';
        
      } catch (error) {
        button.textContent = 'Error - Try Again';
        button.disabled = false;
        
        setTimeout(() => {
          button.textContent = originalText;
        }, 2000);
      }
    });
  }
  
  // Listen for variant changes
  variantManager.onChange((data) => {
    console.log('Variant changed:', data);
    
    // Update availability
    const addToCartBtn = document.getElementById('addToCartBtn');
    if (addToCartBtn) {
      if (data.variant && data.variant.available) {
        addToCartBtn.disabled = false;
        addToCartBtn.textContent = '🛒 Add to Basket';
      } else {
        addToCartBtn.disabled = true;
        addToCartBtn.textContent = 'Out of Stock';
      }
    }
    
    // Update variant ID in form
    const variantIdInput = document.getElementById('variantId');
    if (variantIdInput && data.variant) {
      variantIdInput.value = data.variant.id;
    }
  });
  
  return variantManager;
}

// Export for use in theme
window.ProductVariantManager = ProductVariantManager;
window.VisualOptionSelector = VisualOptionSelector;
window.initProductConfigurator = initProductConfigurator;
